
import { ErrorMessage, Field, Form } from 'vee-validate';
import { computed, inject, ref, watch } from 'vue';
import { ReturnFieldComponent } from './returns-common.mjs';

const AddressFormComponent = {
	props: {
		address: Object,
	},
	emit: [
		'update:address'
	],
	setup( props, { emit } ) {
		const address = ref( { ...props.address } );
		watch( address.value, value => emit( 'update:address', value ) );
		return {
			address
		};
	},
	template: `
		<div class="w-full input-row">
			<ReturnFieldComponent v-model="address.name" type="text" name="name" rules="required" label="Nome" :maxlength="25" />
			<ReturnFieldComponent v-model="address.surname" type="text" name="surname" rules="required" label="Cognome" :maxlength="25" />
		</div>

		<div class="w-full input-row">
			<ReturnFieldComponent v-model="address.street" type="text" name="street" rules="required" label="Indirizzo" :maxlength="100" />
			<ReturnFieldComponent v-model="address.number" type="text" name="number" rules="required" label="Civico" :maxlength="5" />
		</div>

		<div class="w-full input-row">
			<ReturnFieldComponent v-model="address.province" type="text" name="province" rules="required" label="Provincia" :maxlength="100" />
			<ReturnFieldComponent v-model="address.city" type="text" name="city" rules="required" label="Città" :maxlength="100" />
			<ReturnFieldComponent v-model="address.zip" type="text" name="zip" rules="required|number" label="CAP" :maxlength="10" />
		</div>

		<div class="w-full input-row">
			<ReturnFieldComponent v-model="address.phone" type="text" name="phone" rules="required" label="Numero di telefono" :maxlength="25" />
			<ReturnFieldComponent v-model="address.location" type="text" name="location" label="Presso (opzionale)" :maxlength="100" />
		</div>
	`,
	components: {
		ReturnFieldComponent,
	}
}

const StoreFormComponent = {
	props: {
		stores: Array,
		store: Object,
	},
	emit: [
		'update:store'
	],
	setup( props, { emit } ) {
		const stores = props.stores;
		const store = computed( {
			get: () => props.store,
			set: v => emit( 'update:store', v ),
		} );
		const isValidStore = ( store ) => {
			if ( !store.identifier )
				return 'Selezionare una delle opzioni indicate.';
			return true;
		};
		const getStoreId = ( store ) => `store_${ store.identifier }`
		return {
			stores,
			store,
			isValidStore,
			getStoreId,
		};
	},
	template: `
		<Field type="radio" name="store" v-slot="{ field, meta, values }" v-model="store" :rules="isValidStore">
			<div class="w-full" :class="meta">
				<fieldset class="fieldset w-full text-left">
					<legend class="px-2 bg-white">Negozio</legend>
					<div v-for="( cur, index ) in stores" class="store-choice">
						<label :for="getStoreId( cur )" class="flex flex-row gap-2 items-center cursor-pointer">
							<input type="radio" :id="getStoreId( cur )" v-bind="field" :value="cur" class="radio" placeholder=" " />
							<div class="flex flex-col">
								<span class="font-bold uppercase">{{ cur.name }}</span>
								<span class="text-sm">{{ cur.address }}</span>
							</div>
						</label>
					</div>
				</fieldset>
				<ErrorMessage name="store" class="error-message" />
			</div>
		</Field>
	`,
	components: {
		Field,
		ErrorMessage,
	}

}

const BankDepositFormComponent = {
	props: {
		bankDeposit: Object,
	},
	emit: [
		'update:bankDeposit'
	],
	setup( props, { emit } ) {
		const bankDeposit = ref( { ...props.bankDeposit } );
		watch( bankDeposit.value, value => emit( 'update:bankDeposit', value ) );
		return {
			bankDeposit
		};
	},
	template: `
		<div class="w-full input-row">
			<ReturnFieldComponent v-model="bankDeposit.iban" type="text" name="iban" rules="required" label="IBAN" :maxlength="50" />
			<ReturnFieldComponent v-model="bankDeposit.owner" type="text" name="owner" rules="required" label="Intestato a" :maxlength="50" />
		</div>
	`,
	components: {
		ReturnFieldComponent,
	}
};

const ReturnsWizardLocationFormComponent = {
	props: {
		submitData: Object,
		returnType: String,
		stores: Array,
		store: Object,
		showBankDeposit: Boolean,
		bankDeposit: Object,
		address: Object,
	},
	emits: [
		'returnCreated',
		'update:bankDeposit',
		'update:address',
		'update:store',
	],
	setup( props, { emit } ) {

		/** @type {import('./returns-api.js').TReturnType} */
		const returnType = props.returnType;

		/** @type {import('./returns-api.js').TReturnAddress} */
		const address = computed( {
			get: () => props.address,
			set: v => emit( 'update:address', v )
		} );
		/** @type {import('./returns-api.js').TReturnStore} */
		const store = computed( {
			get: () => props.store,
			set: v => emit( 'update:store', v )
		} );
		/** @type {import('./returns-api.js').TReturnBankDeposit} */
		const bankDeposit = computed( {
			get: () => props.bankDeposit,
			set: v => emit( 'update:bankDeposit', v )
		} );

		const stores = props.stores;

		/** @type {boolean} */
		const showBankDeposit = props.showBankDeposit;

		/** @type {import('./returns-api.mjs').ReturnsApi} */
		const returnsApi = inject( 'returnsApi' );

		/** @type {import('./returns-translations.mjs').TReturnsTranslate} **/
		const translate = inject( 'translate' );

		const submitted = ref( false );
		const submitting = ref( false );
		const error = ref( null );

		const submit = async () => {

			if ( submitting.value )
				return;
			submitting.value = true;
			submitted.value = true;
			error.value = null;

			/** @type {import('./returns-api.js').TApiResult<import('./returns-api.js').TCreateReturnResult>} */
			let createRequest;
			try {
				createRequest = await returnsApi.createReturn( props.submitData );
			} catch ( err ) {
				// These are network-level errors.
				console.error( 'An error occurred during creation request', err );
				submitting.value = false;
				error.value = { code: 'NETWORK_ERROR' };
				return;
			}

			if ( !createRequest.success ) {
				submitting.value = false;
				error.value = createRequest.error;
				return;
			}

			console.info( 'Request created succesfully', createRequest.data );
			emit( 'returnCreated', createRequest.data );
			submitting.value = false;

		};

		return {
			returnType,
			address,
			stores,
			store,
			showBankDeposit,
			bankDeposit,
			submit,
			submitted,
			submitting,
			error,
			translate,
		};
	},
	template: `
		<Form v-slot="{ meta }" @submit="submit">
			<div class="form" :class="{ valid: meta.valid, invalid: !meta.valid, submitted, submitting }">
				<div class="wizard-step-body">

					<div v-if="error" class="banner banner--error">
						{{ translate( 'ERRORS.' + error.code, error.parameters ) }}
					</div>
					
					<template v-if="returnType === 'HOME'">
						<AddressFormComponent v-model:address="address" />
					</template>
					<template v-else-if="returnType === 'STORE'">
						<StoreFormComponent :stores="stores" v-model:store="store" />
					</template>
					<template v-else-if="returnType === 'SELFSERVICE'">
						Istruzioni per reso autonomo.
					</template>
					<template v-else>
						TODO: Unsupported return type
					</template>
					
					<BankDepositFormComponent v-if="showBankDeposit" v-model:bankDeposit="bankDeposit" />
				</div>
				<div class="wizard-step-footer">
					<button type="submit" class="button button--primary" :disabled="submitting">
						{{ submitting ? 'Invio richiesta in corso...' : 'Invia richiesta di reso' }}
					</button>
				</div>
			</div>
		</Form>
	`,
	components: {
		Form,
		AddressFormComponent,
		StoreFormComponent,
		BankDepositFormComponent,
	}
}

export default ReturnsWizardLocationFormComponent;
